// Global packages and components
import { clsx } from 'clsx'
import { useEffect } from 'react'
import {
  Slider,
  ProductCard,
  PortraitBanner,
  Heading,
  ContentEditor,
} from '~/components'
import { useProducts } from '~/hooks'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type {
  PortraitBannerProps,
  ProductSearchItem,
  SelectOption,
} from '~/types'

interface BlockLogoCarouselProps {
  content?: PortableTextBlock[]
  title: string
  productCodes: string[]
  addBanner?: boolean
  addFilters?: boolean
  banner?: PortraitBannerProps
  filters?: SelectOption[]
  /** Resolved in the route loader. Null or absent falls back to the client fetch */
  serverProducts?: ProductSearchItem[] | null
}

// Main export
export default function BlockProductCarousel({
  content,
  title,
  productCodes,
  addBanner,
  banner,
  serverProducts,
}: BlockLogoCarouselProps) {
  // Products call
  const { products: fetchedProducts, getProducts } = useProducts()

  // Server products render in the HTML. Recently viewed passes none, and a
  // Brenda failure passes null, so both fetch after hydration instead
  const hasServerProducts = Array.isArray(serverProducts)
  const products = hasServerProducts ? serverProducts : fetchedProducts

  // Side banner
  const hasBanner = addBanner && banner?.image?.asset ? true : false

  // Get the products on mount, unless the server already has
  useEffect(() => {
    if (hasServerProducts) return

    getProducts({ productCodes: productCodes, returnFilters: false })
    // @NOTE: ONLY USE THE BELOW TO SURPRESS FETCHER WARNINGS
    // ..IF YOUR SURE THE FETCHER ISNT CHANGING BETWEEN RENDERS
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasProducts = !!products && products.length > 0

  // When the products load in the browser, the optional intro renders on its
  // own straight away (and in the server HTML) while the carousel waits for
  // them. Without an intro nothing shows until there are products
  if (!hasProducts && !content) return null

  // Search returns products by relevance, so put them back in the order the
  // codes were supplied (CMS order, or newest-first for recently viewed).
  // A product matches on its variant code or its parent code, whichever was used.
  const upperCodes = productCodes.map(c => c.toUpperCase())

  const codeRank = (p: ProductSearchItem): number => {
    const ranks = [p.variantStockCode as string | undefined, p.url?.stockCode]
      .map(code => upperCodes.indexOf((code ?? '').toUpperCase()))
      .filter(index => index !== -1)

    return ranks.length ? Math.min(...ranks) : Number.MAX_SAFE_INTEGER
  }

  const orderedProducts = [...(products ?? [])].sort(
    (a, b) => codeRank(a) - codeRank(b)
  )

  // No microdata here: product pages describe their product in JSON-LD only,
  // and a second, partial Product per card would compete with it
  return (
    <div className="container">
      {content && (
        <div className="mb-6 max-w-2xl">
          <ContentEditor content={content} />
        </div>
      )}
      {hasProducts && (
        <>
          {title && (
            <Heading
              className="mb-10 text-center"
              tag="h2"
              size="h4"
            >
              {title}
            </Heading>
          )}
          <div className="lg:flex">
            <div className="hidden lg:block">
              {addBanner && <PortraitBanner {...banner} />}
            </div>
            <div
              className={clsx({
                'lg:w-full': !hasBanner,
                'lg:w-[calc(100%_-_(20rem_+_1.5rem))]': hasBanner,
              })}
            >
              <Slider
                className="slider-products w-full"
                arrowsInline
                options={{
                  arrows: true,
                  pagination: false,
                  perMove: 1,
                  autoWidth: true,
                  gap: '1.5rem',
                }}
              >
                {orderedProducts.map((p, i) => {
                  // Stable key from the product's own code, falling back to position
                  const key =
                    (p.variantStockCode as string | undefined) ??
                    (p.url?.stockCode as string | undefined) ??
                    i

                  return (
                    <div
                      key={key}
                      className="h-full w-80"
                    >
                      <ProductCard {...p} />
                    </div>
                  )
                })}
              </Slider>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
